// Parent Progress tab: Life Skills Scenario Report Card — which skills the
// child practiced, how choices grew over time, strengths vs needs-practice,
// and independence. Encouraging + concise, with an expandable detail view.
import { useEffect, useState } from "react";
import { HeartHandshake } from "lucide-react";
import { api } from "../../lib/api";

const GROWTH = {
  getting_started: "Just getting started — every scenario builds the habit!",
  growing: "Choices are getting wiser over time — wonderful growth!",
  strong_and_steady: "Consistently wise choices — strong and steady!",
  steady: "Practicing steadily — more scenarios will keep growing confidence.",
};

const outcomeLabel = (s) =>
  s.wise === true ? "made a wise choice"
    : s.retries > 0 ? "explored ideas with a retry" : "practiced the skill";

export const LifeSkillsReportCard = () => {
  const [reports, setReports] = useState([]);
  const [open, setOpen] = useState(null);
  useEffect(() => {
    api.get("/life/family/scenario-report")
      .then(({ data }) => setReports(data.reports || []))
      .catch(() => setReports([]));
  }, []);
  if (!reports.length) return null;
  return (
    <section className="bg-card rounded-2xl border border-border p-5"
      data-testid="life-report-card">
      <h2 className="text-base md:text-lg font-bold font-display flex items-center gap-2 mb-1">
        <HeartHandshake className="h-5 w-5 text-rose-500" aria-hidden="true" />
        Life Skills Report Card
      </h2>
      <p className="text-xs text-muted-foreground mb-3">
        From practice scenarios with Pip — skills, growth and independence (never the conversation itself).
      </p>
      <div className="space-y-3">
        {reports.map((r) => (
          <div key={r.child_id} className="rounded-xl border border-border p-3 space-y-2"
            data-testid={`life-report-${r.child_id}`}>
            <div className="flex justify-between gap-2 text-sm flex-wrap">
              <span className="font-bold">
                {r.child_name} — {r.total} scenario{r.total === 1 ? "" : "s"} · {r.skills_practiced.length} life skill{r.skills_practiced.length === 1 ? "" : "s"}
              </span>
              <span className="text-xs text-muted-foreground" data-testid={`life-report-independence-${r.child_id}`}>
                {r.independent} handled independently · {r.with_support} with Pip's support
              </span>
            </div>
            <p className="text-xs text-emerald-700 font-semibold" data-testid={`life-report-growth-${r.child_id}`}>
              {GROWTH[r.growth] || GROWTH.getting_started}
            </p>
            <div className="flex flex-wrap gap-1.5">
              {r.strengths.map((t) => (
                <span key={t} className="text-[11px] font-bold rounded-full bg-emerald-100 text-emerald-900 px-2.5 py-0.5">
                  Strength: {t}
                </span>
              ))}
              {r.needs_practice.map((t) => (
                <span key={t} className="text-[11px] font-bold rounded-full bg-amber-100 text-amber-900 px-2.5 py-0.5">
                  Keep practicing: {t}
                </span>
              ))}
            </div>
            <div className="flex flex-wrap gap-1.5">
              {r.skills_practiced.map((s) => (
                <span key={s} className="text-[11px] rounded-full bg-rose-50 border border-rose-200 text-rose-900 px-2.5 py-0.5">
                  {s}
                </span>
              ))}
            </div>
            <button type="button"
              onClick={() => setOpen(open === r.child_id ? null : r.child_id)}
              className="text-xs font-bold text-primary underline underline-offset-2"
              data-testid={`life-report-toggle-${r.child_id}`}>
              {open === r.child_id ? "Hide details" : "View details"}
            </button>
            {open === r.child_id && (
              <ul className="space-y-1" data-testid={`life-report-detail-${r.child_id}`}>
                {r.recent.map((s, i) => (
                  <li key={i} className="text-xs text-muted-foreground flex justify-between gap-2">
                    <span><span className="font-semibold text-foreground">{s.title}</span> ({s.skill}) — {outcomeLabel(s)}</span>
                    <span className="shrink-0">{(s.at || "").slice(0, 10)}</span>
                  </li>
                ))}
              </ul>
            )}
          </div>
        ))}
      </div>
    </section>
  );
};
